import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChallengeAction } from '@sss/types';
import { CHALLENGE_URL } from '../lib/config';
import { challengeFrameUrl, challengeTheme, parseChallengeMessage } from '../lib/humanCheck';

export type HumanCheckState = 'checking' | 'interactive' | 'ready' | 'failed' | 'unavailable';

/** The challenge origin, or null if it's missing, malformed or (never allowed) this page's own origin. */
const challengeOrigin = (): string | null => {
  try {
    const origin = new URL(CHALLENGE_URL).origin;
    return origin !== window.location.origin ? origin : null;
  } catch {
    return null;
  }
};

/**
 * Runs the human check in its frame and holds the latest token. `take()`
 * hands the token over (tokens are single use) and asks the frame for the
 * next one.
 */
export function useHumanCheck(action: ChallengeAction) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const tokenRef = useRef<string | null>(null);
  const origin = useMemo(challengeOrigin, []);
  const [state, setState] = useState<HumanCheckState>(origin ? 'checking' : 'unavailable');
  const [attempt, setAttempt] = useState(0);

  const src = useMemo(
    () => (origin ? challengeFrameUrl(origin, window.location.origin, action, challengeTheme(document.documentElement)) : null),
    [origin, action],
  );

  useEffect(() => {
    if (!origin) return;
    const onMessage = (event: MessageEvent) => {
      // Only our frame, from the challenge origin. Anything else is ignored.
      if (event.origin !== origin || event.source !== frameRef.current?.contentWindow) return;
      const message = parseChallengeMessage(event.data);
      if (!message) return;
      if (message.type === 'token') {
        tokenRef.current = message.token;
        setState('ready');
      } else {
        tokenRef.current = null;
        setState(message.type === 'expired' ? 'checking' : message.type === 'interactive' ? 'interactive' : 'failed');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origin]);

  /** The current token, or null if the check hasn't passed. The frame starts on a fresh one. */
  const take = useCallback((): string | null => {
    const token = tokenRef.current;
    if (!token || !origin) return null;
    tokenRef.current = null;
    setState('checking');
    frameRef.current?.contentWindow?.postMessage({ type: 'reset' }, origin);
    return token;
  }, [origin]);

  /** Reload the frame after a failure (e.g. a blocker stopped Turnstile loading). */
  const retry = useCallback(() => {
    tokenRef.current = null;
    setState('checking');
    setAttempt((n) => n + 1);
  }, []);

  return { frameRef, src, state, attempt, take, retry };
}

export type HumanCheck = ReturnType<typeof useHumanCheck>;
